import { createLineSwap, paintFailure } from './cart-line-swap.js';

const STORAGE_KEY = 'fn-cart-upsell-expanded';

export function createCartUpsell({ root, cart, fetch, storage, track, notify, pathname, sheet }) {
  const drawer = root.getElementById('cart-drawer');
  if (!drawer || !cart || typeof cart.enqueueWrite !== 'function') return null;

  const addUrl = drawer.dataset.cartAddUrl;
  const changeUrl = drawer.dataset.cartChangeUrl;
  const copy = {
    added: drawer.dataset.noticeAdded,
    removed: drawer.dataset.noticeRemoved,
  };

  const say = (title, subtitle, group) => { if (title) notify({ title, subtitle: subtitle || '', group }); };

  function readExpanded() {
    try {
      const list = JSON.parse(storage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  function writeExpanded(list) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
    }
  }

  const accordions = () => Array.from(drawer.querySelectorAll('[data-upsell]'));

  function setCollapsed(details, collapsed) {
    details.open = true;
    details.classList.toggle('is-collapsed', collapsed);
    const body = details.querySelector('.cart-upsell__body');
    if (body) body.inert = collapsed;
    const summary = details.querySelector('[data-action="upsell-toggle"]');
    if (summary) summary.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  }

  function applyCollapsed() {
    const expanded = new Set(readExpanded());
    accordions().forEach((details) => {
      setCollapsed(details, !expanded.has(details.dataset.group));
    });
  }

  function toggleAccordion(details) {
    const collapsed = !details.classList.contains('is-collapsed');
    setCollapsed(details, collapsed);
    const rest = readExpanded().filter((group) => group !== details.dataset.group);
    writeExpanded(collapsed ? rest : rest.concat(details.dataset.group));
    if (collapsed) track('cart_upsell_collapse', { family: details.dataset.family });
  }

  function trackView() {
    accordions().forEach((details) => {
      const cards = Array.from(details.querySelectorAll('[data-offer]')).map((card) => card.dataset.offer);
      track('cart_upsell_view', {
        family: details.dataset.family,
        cards,
        collapsed: details.classList.contains('is-collapsed'),
      });
    });
  }

  async function post(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: 'cart-drawer', sections_url: pathname }),
    });
    if (!response.ok) throw new Error('cart-upsell: write failed');
    return response.json();
  }

  const sectionOf = (data) => data && data.sections && data.sections['cart-drawer'];

  function liveCard(group, addon) {
    const details = accordions().find((el) => el.dataset.group === group);
    if (!details) return null;
    return details.querySelector(`[data-offer="${addon}"] .cart-upsell__cta`);
  }

  function addedSibling(button) {
    const { group, addon, slot } = button.dataset;
    const details = accordions().find((el) => el.dataset.group === group);
    if (!details || !slot) return null;
    return Array.from(details.querySelectorAll('[data-action="upsell-remove"]'))
      .find((other) => other.dataset.addon !== addon && other.dataset.slot === slot) || null;
  }

  const familyOf = (button) => {
    const details = button.closest('[data-upsell]');
    return details ? details.dataset.family : '';
  };

  const pending = new Set();
  const pendingId = (group, addon) => `${group}\u0000${addon}`;

  function markBusy() {
    accordions().forEach((details) => {
      details.querySelectorAll('[data-offer] .cart-upsell__cta').forEach((button) => {
        const busy = pending.has(pendingId(button.dataset.group, button.dataset.addon));
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    });
  }

  const cartJsonUrl = (drawer.dataset.cartUrl || '/cart') + '.js';

  async function readCart() {
    const response = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart-upsell: cart read failed');
    return response.json();
  }

  const swapOut = createLineSwap({ post, changeUrl, readCart });

  function render(data) {
    if (!cart.replaceContent(sectionOf(data))) cart.reload();
  }

  function write(button, task) {
    const id = pendingId(button.dataset.group, button.dataset.addon);
    if (pending.has(id)) return Promise.resolve();
    pending.add(id);
    markBusy();
    cart.hideError();
    return cart.enqueueWrite(async () => {
      try {
        await task();
      } finally {
        pending.delete(id);
        markBusy();
      }
    });
  }

  function addBox(button) {
    const { addon, group, variantId, priceCents } = button.dataset;
    const family = familyOf(button);
    return write(button, async () => {
      if (!liveCard(group, addon)) return;
      let painted = null;
      try {
        painted = await post(addUrl, {
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: { _group: group, _addon: addon, _addon_origin: 'cart' },
          }],
        });
        const other = addedSibling(button);
        if (other && other.dataset.lineKey) {
          painted = await swapOut(other.dataset.lineKey, painted);
        }
        track('cart_upsell_add', { addon, value_cents: Number(priceCents) || 0, origin: 'cart', family });
        render(painted);
        say(copy.added, button.dataset.noticeSubtitle, group);
      } catch (error) {
        paintFailure({ cart, render }, error, painted);
      }
    });
  }

  function removeBox(button) {
    const { addon, group } = button.dataset;
    const family = familyOf(button);
    return write(button, async () => {
      const card = liveCard(group, addon);
      const lineKey = card && card.dataset.lineKey;
      if (!lineKey) return;
      const name = card.dataset.noticeName;
      try {
        const data = await post(changeUrl, { id: lineKey, quantity: 0 });
        track('cart_upsell_remove', { addon, family });
        render(data);
        say(copy.removed, name, group);
      } catch {
        cart.showError();
      }
    });
  }

  function openSheet(trigger) {
    const provider = typeof sheet === 'function' ? sheet() : sheet;
    const card = liveCard(trigger.dataset.group, trigger.dataset.addon);
    if (provider && card) provider.open(card, { opener: trigger });
  }

  drawer.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-action]');
    if (!trigger || !drawer.contains(trigger)) return;
    switch (trigger.dataset.action) {
      case 'upsell-toggle': {
        event.preventDefault();
        const details = trigger.closest('[data-upsell]');
        if (details) toggleAccordion(details);
        break;
      }
      case 'upsell-add':
        addBox(trigger);
        break;
      case 'upsell-remove':
        removeBox(trigger);
        break;
      case 'upsell-zoom':
        event.preventDefault();
        openSheet(trigger);
        break;
      default:
        break;
    }
  });

  drawer.addEventListener('cart:rendered', () => {
    applyCollapsed();
    markBusy();
  });
  drawer.addEventListener('cart:opened', trackView);
  applyCollapsed();
  if (drawer.dataset.opened === 'announced') trackView();

  return { applyCollapsed };
}

if (typeof document !== 'undefined') {
  const start = () => createCartUpsell({
    root: document,
    cart: window.fnCart,
    fetch: (url, init) => window.fetch(url, init),
    storage: window.sessionStorage,
    track: (event, payload) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...payload });
    },
    notify: (payload) => {
      if (window.fnCartNotice) window.fnCartNotice.show(payload);
    },
    pathname: window.location.pathname,
    sheet: () => window.fnCartSheet,
  });
  if (window.fnCart) start();
  else document.addEventListener('fn:cart-ready', start, { once: true });
}
